export const SKILL_IDS = [
  "digging",
  "fetching",
  "sniffing",
  "begging",
  "toycraft",
  "treatcraft",
] as const;
export type SkillId = (typeof SKILL_IDS)[number];

export type SkillGroup = "gathering" | "making";

export interface SkillDefinition {
  id: SkillId;
  /** Stable backend id - never rename this field. */
  backendId: "Digging" | "Fetching" | "Sniffing" | "Begging" | "Toycraft" | "Treatcraft";
  /** Player-facing name (safe to rename later). */
  name: string;
  verb: string;
  description: string;
  /** Accent color. */
  color: number;
  group: SkillGroup;
}

export const SKILLS: Record<SkillId, SkillDefinition> = {
  digging: {
    id: "digging",
    backendId: "Digging",
    name: "Digging",
    verb: "Digging",
    description: "Unearth bones, pebbles, and shiny treasures for toys and treats.",
    // Soft slate-blue - earth & stone under park sky.
    color: 0x6b8cae,
    group: "gathering",
  },
  fetching: {
    id: "fetching",
    backendId: "Fetching",
    name: "Fetching",
    verb: "Fetching",
    description: "Retrieve sticks and branches: handles for better toys.",
    // Meadow leaf green.
    color: 0x5a9a3a,
    group: "gathering",
  },
  sniffing: {
    id: "sniffing",
    backendId: "Sniffing",
    name: "Sniffing",
    verb: "Sniffing",
    description: "Nose-down for fallen snacks and picnic scraps.",
    // Berry / flower pink.
    color: 0xc45b7a,
    group: "gathering",
  },
  begging: {
    id: "begging",
    backendId: "Begging",
    name: "Begging",
    verb: "Charming",
    description: "Charm humans for pets and handouts: rare ingredients for Treatcraft.",
    // Warm coral blush.
    color: 0xe08a6a,
    group: "gathering",
  },
  toycraft: {
    id: "toycraft",
    backendId: "Toycraft",
    name: "Toycraft",
    verb: "Crafting",
    description: "Forge dig finds and sticks into better shovels and balls.",
    // Forge ember orange.
    color: 0xd06a2e,
    group: "making",
  },
  treatcraft: {
    id: "treatcraft",
    backendId: "Treatcraft",
    name: "Treatcraft",
    verb: "Mixing",
    description: "Mix dig finds, snacks, and handouts into boosting treats.",
    // Honey / biscuit gold.
    color: 0xd4a24a,
    group: "making",
  },
};

/** CSS hex for a skill accent (e.g. `#6b8cae`). */
export function skillColorCss(id: SkillId): string {
  return `#${SKILLS[id].color.toString(16).padStart(6, "0")}`;
}

export const SKILL_GROUPS: { id: SkillGroup; name: string; skillIds: SkillId[] }[] = [
  { id: "gathering", name: "Gathering", skillIds: ["digging", "fetching", "sniffing", "begging"] },
  { id: "making", name: "Making", skillIds: ["toycraft", "treatcraft"] },
];

export interface SkillState {
  xp: number;
  level: number;
}

export function emptySkills(): Record<SkillId, SkillState> {
  return {
    digging: { xp: 0, level: 1 },
    fetching: { xp: 0, level: 1 },
    sniffing: { xp: 0, level: 1 },
    begging: { xp: 0, level: 1 },
    toycraft: { xp: 0, level: 1 },
    treatcraft: { xp: 0, level: 1 },
  };
}

/** 6 skills × 99. */
export const MAX_TOTAL_LEVEL = 594;

export function totalLevel(skills: Record<SkillId, SkillState>): number {
  return SKILL_IDS.reduce((sum, id) => sum + (skills[id]?.level ?? 1), 0);
}

export const LEVEL_MILESTONES = [10, 20, 30, 40, 50, 60, 70, 80, 90, 99] as const;

/**
 * RuneScape-style action speed: higher level → shorter channel.
 * Level 1 = 1.0× duration, level 99 ≈ 0.4× duration (~2.5× faster).
 */
export function skillActionSpeedMultiplier(level: number): number {
  const clamped = Math.max(1, Math.min(99, Math.floor(level)));
  return 1 - ((clamped - 1) / 98) * 0.6;
}
